<script>
import countries from '@/constants/countries';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import relationships from '@/components/bookVisit/relationships';
import VisitorService from '@/services/VisitorService';
import ContactService from '@/services/ContactService';
import { logger } from '@/util/logger';
import { getErrorInfo } from '@/util/helpers';
import { ERROR_CODES } from '@/util/errorCodes';

extend('required', {
  ...required,
  message: 'This field is required',
});

extend('olderThan18', {
  validate(value) {
    const today = new Date();
    const date18YearsAgo = new Date();
    date18YearsAgo.setFullYear(today.getFullYear() - 18);

    return new Date(value) < date18YearsAgo;
  },
  message: 'You must be at least 18 years old',
});

export default {
  name: 'CreateVisitor',
  data() {
    return {
      loading: false,
      visitor: {
        address: {},
      },
      contact: {
        prisoner: {},
      },
      pageIndex: 0,
    };
  },
  computed: {
    initialDate18YearsAgo() {
      const today = new Date();
      const year = today.getFullYear() - 18;
      const month = today.getMonth();
      const day = today.getDate();
      return new Date(year, month, day);
    },
    pageTitle() {
      let title = 'Create Visitor';
      if (this.pageIndex === 1) {
        title = 'Create Contact';
      }
      return title;
    },
    pageSubtitle() {
      let subtitle = 'Use the form below to create a new visitor for on-demand visits';
      if (this.pageIndex === 1) {
        subtitle = 'Use the form below to add a contact for the visitor\'s account';
      }
      return subtitle;
    },
  },
  methods: {
    relationships() {
      return relationships.map(r => ({ value: r, text: `They are my ${r}` }));
    },
    countries() {
      return countries.map(c => ({ value: c.value, text: c.text }))
    },
    async validateVisitor() {
      if (!await this.$refs.createVisitor.validate()) {
        return;
      }
      this.goToPage(1);
    },
    async validateContact() {
      if (!await this.$refs.createContact.validate()) {
        return;
      }

      await this.submitDetails();
    },
    async submitDetails() {
      try {
        this.loading = true;

        // Create the visitor
        const { data } = await VisitorService.registerOnDemandVisitor(this.visitor);
        const userId = data.user_id;

        // Create the contact
        this.contact.user_id = userId;
        await ContactService.createContact(this.contact);

        this.$toast.success('Visitor successfully registered');
        this.$router.push({ name: 'visitor-profile', params: { id: userId } })
      } catch (err) {
        logger.error('Error creating visitor', err, { dto: this.visitor }, 'CreateVisitor:submitDetails');

        let message = 'Unable to create visitor. Please check the details you have entered and try again';
        const { error } = getErrorInfo(err);
        if (error === ERROR_CODES.ENTITY.ALREADY_EXISTS) {
          message = 'Unable to create visitor with those details, as they already exist';
        }
        this.$toast.error(`${message}`, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    goToPage(index) {
      this.pageIndex = index;
      window.scrollTo(0, 0);
    },
  },
}
</script>

<template>
  <div>
    <h2 cy="page-title" class="font-weight-bolder">{{ pageTitle }}</h2>
    <p>{{ pageSubtitle }}</p>
    <!-- Visitor -->
    <validation-observer v-if="pageIndex === 0" ref="createVisitor">
      <b-form>
        <b-row>
          <b-col cols="12">
            <b-card title="Personal details">
              <b-form-group label="First name*">
                <validation-provider
                  key="visitor-first-name"
                  #default="{ errors }"
                  name="first-name"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-first-name"
                    v-model="visitor.firstname"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their first name"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Last name*">
                <validation-provider
                  key="visitor-last-name"
                  #default="{ errors }"
                  name="last-name"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-last-name"
                    v-model="visitor.lastname"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their last name"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Date of birth*">
                <validation-provider
                  key="visitor-dob"
                  #default="{ errors }"
                  name="date-of-birth"
                  rules="required|olderThan18"
                >
                  <b-form-datepicker
                    id="visitor-dob"
                    v-model="visitor.dob"
                    :initial-date="initialDate18YearsAgo"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Select their date of birth"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Email*">
                <validation-provider
                  key="visitor-email"
                  #default="{ errors }"
                  name="email"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-email"
                    v-model="visitor.email"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their email address"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Phone number*">
                <validation-provider
                  key="visitor-phone"
                  #default="{ errors }"
                  name="phone-number"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-phone"
                    v-model="visitor.phone_number"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their phone number"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
            </b-card>
            <b-card title="Address Info">
              <b-form-group label="Address line 1*">
                <validation-provider
                  key="visitor-address-1"
                  #default="{ errors }"
                  name="address-line-1"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-address-1"
                    v-model="visitor.address.address1"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the first line of their address"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Address line 2*">
                <validation-provider
                  key="visitor-address-2"
                  #default="{ errors }"
                  name="address-line-2"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-address-2"
                    v-model="visitor.address.address2"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the first line of their address"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Address line 3">
                <validation-provider
                  key="visitor-address-3"
                  #default="{ errors }"
                  name="address-line-3"
                >
                  <b-form-input
                    id="visitor-address-3"
                    v-model="visitor.address.address3"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter the first line of their address"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Town/City*">
                <validation-provider
                  key="visitor-address-city"
                  #default="{ errors }"
                  name="town-city"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-address-city"
                    v-model="visitor.address.city_town"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their town/city"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="County/Region*">
                <validation-provider
                  key="visitor-address-region"
                  #default="{ errors }"
                  name="county"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-address-region"
                    v-model="visitor.address.region"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their county"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Zip/Postcode*">
                <validation-provider
                  key="visitor-address-postcode"
                  #default="{ errors }"
                  name="postcode"
                  rules="required"
                >
                  <b-form-input
                    id="visitor-address-postcode"
                    v-model="visitor.address.zip_postal_code"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their postcode"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Country*">
                <validation-provider
                  key="visitor-address-country"
                  #default="{ errors }"
                  name="country"
                  rules="required"
                >
                  <b-form-select
                    id="visitor-address-country"
                    v-model="visitor.address.country"
                    :state="errors.length > 0 ? false:null"
                    :options="countries()"
                    placeholder="Select country"
                    type="select"
                  />
                </validation-provider>
              </b-form-group>
            </b-card>
            <div class="d-flex">
              <div class="ui-spacer" />
              <b-btn
                variant="primary"
                @click="validateVisitor"
              >
                Next
              </b-btn>
            </div>
          </b-col>
        </b-row>

      </b-form>
    </validation-observer>

    <!-- Contact -->
    <validation-observer v-else ref="createContact">
      <b-form>
        <b-row>
          <b-col cols="12">
            <b-card title="Contact details">
              <b-form-group label="Prisoner number*">
                <validation-provider
                  key="contact-prisoner-number"
                  #default="{ errors }"
                  name="contact-prisoner-number"
                  rules="required"
                >
                  <b-form-input
                    id="contact-prisoner-number"
                    v-model="contact.prisoner_number"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their prisoner number"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="First name*">
                <validation-provider
                  key="contact-first-name"
                  #default="{ errors }"
                  name="contact-first-name"
                  rules="required"
                >
                  <b-form-input
                    id="contact-first-name"
                    v-model="contact.prisoner.firstname"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their first name"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Last name*">
                <validation-provider
                  key="contact-last-name"
                  #default="{ errors }"
                  name="contact-last-name"
                  rules="required"
                >
                  <b-form-input
                    id="contact-last-name"
                    v-model="contact.prisoner.lastname"
                    :state="errors.length > 0 ? false:null"
                    placeholder="Enter their first name"
                    type="text"
                  />
                </validation-provider>
              </b-form-group>
              <b-form-group label="Relationship*">
                <validation-provider
                  key="contact-relationship"
                  #default="{ errors }"
                  name="contact-relationship"
                  rules="required"
                >
                  <b-form-select
                    id="contact-relationship"
                    v-model="contact.relationship"
                    :state="errors.length > 0 ? false:null"
                    :options="relationships()"
                    placeholder="Select their relationship"
                    type="select"
                  />
                </validation-provider>
              </b-form-group>
            </b-card>
            <div class="d-flex">
              <b-btn
                :disabled="loading"
                variant="outline-primary"
                @click="goToPage(0)"
              >
                Back
              </b-btn>
              <div class="ui-spacer" />
              <b-btn
                :disabled="loading"
                variant="primary"
                @click="validateContact"
              >
                {{ loading ? 'Loading...' : 'Complete' }}
              </b-btn>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </div>
</template>
